import React, { useMemo } from 'react';

import { stringify } from 'query-string';
import { useTranslation } from 'react-i18next';

import { NoDataEnDash } from '@packages/components/no-data-en-dash/no-data-en-dash';
import { LinkRouter } from '@packages/fenrir/components';
import { Link } from '@packages/fenrir/components/link';

import { useRiskManagementDetails } from '../../hooks/use-risk-management-details/use-risk-management-details';

interface RiskLinkProps {
  assetId: string;
  value?: number;
}

const defaultQueryParams = stringify(
  {
    fromLevel: 4,
    toLevelInclusive: 10,
    state: [
      'Mitigation/Fix in Progress',
      'Acknowledged, In-Planning',
      'Unsuccessful Validation',
      'Fixed, Waiting Validation',
      'Open',
    ],
    status: ['Active', 'Inactive'],
  },
  { sort: false },
);

const defaultQueryParamsUP = stringify({
  sort: '-score',
  status: 'unresolved',
});

export const RiskLink: React.FC<RiskLinkProps> = ({ assetId, value }) => {
  const { t } = useTranslation('ads');
  const isInteger = Number.isInteger(value);
  const { isRisksAvailableInUP, getRiskManagementEndpoint } =
    useRiskManagementDetails();

  const riskEndpoint = useMemo(
    () =>
      getRiskManagementEndpoint(
        assetId,
        isRisksAvailableInUP ? defaultQueryParamsUP : defaultQueryParams,
      ),
    [assetId, getRiskManagementEndpoint, isRisksAvailableInUP],
  );

  if (isRisksAvailableInUP) {
    return (
      <LinkRouter to={riskEndpoint} variant="body2">
        {t('assetDetails.vulnerabilities.viewRisks')}
      </LinkRouter>
    );
  }

  if (isInteger)
    return (
      <Link
        href={riskEndpoint}
        target="_blank"
        variant="body2"
        rel="noopener noreferrer"
      >
        {value}
      </Link>
    );

  return <NoDataEnDash />;
};
